import React, { useState } from "react";
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Check as CheckIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon,
} from "@mui/icons-material";
import { useQuery } from "@tanstack/react-query";
import apiService from "../../services/api";
import { MediaKitSectionTitle } from "./MediaKitSettingsSection";
import { MediaKitStatusPill } from "./MediaKitStatusPill";
import { useLocale } from "../../i18n/LocaleProvider";
import { withEnglishQuery } from "../../i18n/locale";
import { useTranslation } from "react-i18next";

export type MediaKitSharePanelProps = {
  podcastSlug: string;
  companySlug?: string | undefined;
  mediaKitUrl: string;
  enabled: boolean;
  savedEnabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  selectedMonths: string[];
};

const CopyButton: React.FC<{ value: string; label: string }> = ({
  value,
  label,
}) => {
  const { t } = useTranslation("mediaKit");
  const [copied, setCopied] = useState(false);
  return (
    <Tooltip title={copied ? t("copied") : label}>
      <IconButton
        size="small"
        aria-label={label}
        onClick={async () => {
          try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
          } catch (err) {
            console.error("Failed to copy URL:", err);
          }
        }}
      >
        {copied ? (
          <CheckIcon fontSize="small" color="success" />
        ) : (
          <ContentCopyIcon fontSize="small" />
        )}
      </IconButton>
    </Tooltip>
  );
};

const UrlRow: React.FC<{ url: string; label?: string }> = ({ url, label }) => {
  const { t } = useTranslation("mediaKit");
  return (
  <Stack direction="row" spacing={0.5} alignItems="center" sx={{ minWidth: 0 }}>
    <Box sx={{ minWidth: 0, flex: 1 }}>
      {label ? (
        <Typography variant="body2" fontWeight={600} noWrap>
          {label}
        </Typography>
      ) : null}
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        title={url}
        sx={{ display: "block" }}
      >
        {url.replace(/^https?:\/\//, "")}
      </Typography>
    </Box>
    <CopyButton value={url} label={t("copyLink")} />
    <Tooltip title={t("open")}>
      <IconButton
        size="small"
        component="a"
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t("open")}
      >
        <OpenInNewIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Stack>
  );
};

export const MediaKitSharePanel: React.FC<MediaKitSharePanelProps> = ({
  podcastSlug,
  companySlug,
  mediaKitUrl,
  enabled,
  savedEnabled,
  onEnabledChange,
  selectedMonths,
}) => {
  const { t } = useTranslation("mediaKit");
  const { locale } = useLocale();
  const monthsKey = [...selectedMonths].sort().join(",");
  const { data: linkedProgramsData, isLoading: linkedProgramsLoading } =
    useQuery({
      queryKey: ["media-kit-linked-programs", podcastSlug, monthsKey],
      queryFn: () =>
        apiService.getPodcastMediaKitLinkedPrograms(
          podcastSlug,
          selectedMonths,
        ),
      enabled: Boolean(podcastSlug) && savedEnabled,
    });

  const programs = linkedProgramsData?.programs ?? [];
  const pendingToggle = enabled !== savedEnabled;

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: "hidden" }}>
      <Box sx={{ p: 2.5 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          spacing={1}
        >
          <Box>
            <MediaKitSectionTitle>{t("publicKit")}</MediaKitSectionTitle>
            <Box sx={{ mt: 0.75 }}>
              <MediaKitStatusPill active={savedEnabled} />
            </Box>
          </Box>
          <Switch
            checked={enabled}
            onChange={(e) => onEnabledChange(e.target.checked)}
            inputProps={{ "aria-label": t("enablePublic") }}
          />
        </Stack>
        <Typography
          variant="body2"
          color={pendingToggle ? "warning.main" : "text.secondary"}
          sx={{ mt: 1 }}
        >
          {pendingToggle
            ? enabled
              ? t("saveToPublish")
              : t("saveToUnpublish")
            : savedEnabled
              ? t("anyoneWithLink")
              : t("enableToShare")}
        </Typography>
      </Box>

      <Divider />

      <Box sx={{ p: 2.5 }}>
        <MediaKitSectionTitle>{t("mainLink")}</MediaKitSectionTitle>
        <Box sx={{ mt: 0.5, opacity: savedEnabled ? 1 : 0.6 }}>
          <UrlRow url={mediaKitUrl} />
        </Box>
      </Box>

      {savedEnabled ? (
        <>
          <Divider />
          <Box sx={{ p: 2.5 }}>
            <MediaKitSectionTitle>{t("programLinks")}</MediaKitSectionTitle>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mb: 1 }}
            >
              {t("sameConfig")}
            </Typography>
            {linkedProgramsLoading ? (
              <Stack direction="row" spacing={1} alignItems="center">
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  {t("loadingPrograms")}
                </Typography>
              </Stack>
            ) : programs.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t("noPrograms")}
              </Typography>
            ) : (
              <Stack
                spacing={1.25}
                divider={<Divider flexItem />}
                sx={{ maxHeight: 320, overflowY: "auto", pr: 0.5 }}
              >
                {programs.map((prog) => {
                  const programUrl = withEnglishQuery(
                    companySlug
                      ? `${window.location.origin}/${encodeURIComponent(companySlug)}/${encodeURIComponent(podcastSlug)}/${encodeURIComponent(prog.slug)}`
                      : `${window.location.origin}/programa/${encodeURIComponent(prog.slug)}/media-kit`,
                    locale,
                  );
                  return (
                    <UrlRow key={prog.slug} url={programUrl} label={prog.name} />
                  );
                })}
              </Stack>
            )}
          </Box>
        </>
      ) : null}
    </Paper>
  );
};
